// src/services/api.ts
import axios, {
  type AxiosInstance,
 type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';

// ============================================
// TYPES
// ============================================

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: 'customer' | 'provider' | 'admin';
  avatar?: string;
  is_verified: boolean;
  // provider specific
  business_name?: string;
  verification_status?: 'pending' | 'verified' | 'rejected' | 'suspended';
  subscription_plan?: 'Basic' | 'Premium' | 'Enterprise';
  subscription_status?: 'active' | 'inactive' | 'expired' | 'trial';
}
export interface Booking {
  id: string;
  customerId: string;
  customerName: string;
  customerAvatar?: string | null;
  customerPhone: string;
  customerEmail?: string;
  providerId: string;
  providerName?: string;
  providerBusiness?: string;
  serviceId?: string;
  serviceName: string;
  servicePrice?: number;
  date: string;
  time: string;  duration: number;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  amount: number;
  discountAmount?: number;
  finalAmount: number;
  paymentStatus: 'pending' | 'paid' | 'refunded' | 'failed';
  paymentMethod: 'mpesa' | 'card' | 'cash';
  customerNotes?: string;
  providerNotes?: string;
  address: string;
  location?: {
    lat: number;
    lng: number;
  };
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
  startedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  rating?: number;
  review?: string;
}
export interface Review {
  id: string;
  customerId: string;
  customerName: string;
  customerAvatar?: string | null;
  customerPhone?: string;
  customerEmail?: string;
  providerId: string;
  providerName?: string;
  bookingId?: string;
  serviceName?: string;
  rating: number;
  comment: string;
  response?: string;
  responseDate?: string;
  date: string;
  helpful: number;
  isVerified: boolean;
  isPublic: boolean;
  status: 'pending' | 'approved' | 'rejected';
  reported: boolean;
  reportReason?: string;
  createdAt: string;
  updatedAt: string;
}
export interface Analytics {
  totalBookings: number;
  completedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  bookingGrowth: number;
  revenueGrowth: number;
  views: number;
  conversionRate: number;
}
export interface AddServiceData {
  serviceName: string;
  categoryId: string;
  description: string;
  price: number;
  discountPrice?: number | null;
  estimatedDuration?: number | null;
  isAvailable?: boolean;
  paymentMethod: 'mpesa' | 'card' | 'bank';
}

export interface UpdateServiceData {
  price?: number;
  discountPrice?: number | null;
  isAvailable?: boolean;
}

export interface ServiceApprovalData {
  rejectionReason?: string;
}
export interface BookingTrend {
  date: string;
  bookings: number;
  revenue: number;
}

export interface CategoryDistribution {
  name: string;
  value: number;
  color?: string;
}

export interface RevenueBreakdown {
  name: string;
  value: number;
  color?: string;
}

export interface AnalyticsResponse {
  overview: Analytics;
  bookingTrend: BookingTrend[];
  categoryDistribution: CategoryDistribution[];
  revenueBreakdown: RevenueBreakdown[];
}
export interface ServiceCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  imageUrl?: string;
  parentId?: string;
  displayOrder: number;
  isActive: boolean;
  children?: ServiceCategory[];
}
export interface LoginCredentials {
  email: string;
  password: string;
}


export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  role: 'customer' | 'provider';
  // Provider specific fields
  businessName?: string;
  businessDescription?: string;
  businessCategory?: string; 
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  message: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

// ============================================
// PROVIDER PROFILE TYPES
// (mirrors the shape returned by getProviderProfile / updateProviderProfile
//  in controllers/provider.controller.js)
// ============================================

export interface ProviderProfileUser {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  profile_image_url?: string | null;
}

export interface ProviderTown {
  id: string;
  name: string;
}

export interface ProviderCounty {
  id: string;
  name: string;
}

export interface ProviderAvailabilityStatus {
  is_available: boolean;
  status_message?: string | null;
  last_updated: string;
}

export interface ProviderBusinessHour {
  id: string;
  day_of_week: number;
  opens_at: string;
  closes_at: string;
  is_closed: boolean;
}

export interface ProviderImageItem {
  id: string;
  image_url: string;
  caption?: string | null;
  is_primary: boolean;
  image_type: 'logo' | 'gallery' | 'certificate';
}

export interface ProviderServiceItem {
  id: string;
  price?: number | null;
  is_available: boolean;
  service: {
    id: string;
    name: string;
    discount_price?:string;
    category: { id: string; name: string };
  };
}

export interface ProviderVerificationDocument {
  id: string;
  document_type: string;
  document_name?: string | null;
  file_url: string;
  created_at:string;
  verification_status: 'pending' | 'approved' | 'rejected';
}

export interface ProviderProfileResponse {
  id: string;
  user_id: string;
  business_name: string;
  business_description?: string | null;
  business_email?: string | null;
  business_phone?: string | null;
  business_website?: string | null;
  whatsapp_number?: string | null;
  landmark?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  town_id?: string | null;
  county_id?: string | null;
  postal_code?: string | null;
  is_24_hours: boolean;
  verification_status: 'pending' | 'verified' | 'rejected' | 'suspended';
  subscription_status: 'active' | 'inactive' | 'expired' | 'trial';
  average_rating: number;
  total_reviews: number;
  response_time: number;
  completion_rate: number;
  booking_count: number;
  founded_year?: number | null;
  employees?: number | null;
  certifications?: string[] | null;
  payment_methods?: string[] | null;
  languages?: string[] | null;
  social_media?: Record<string, string> | null;
  user: ProviderProfileUser;
  town?: ProviderTown | null;
  location?:string| null;
  county?: ProviderCounty | null;
  availability_status?: ProviderAvailabilityStatus | null;
  business_hours: ProviderBusinessHour[];
  provider_images: ProviderImageItem[];
  provider_services: ProviderServiceItem[];
  verification_documents: ProviderVerificationDocument[];
  created_at:Date;
}

export interface UpdateProviderProfileData {
  businessName?: string;
  businessDescription?: string;
  businessEmail?: string;
  businessPhone?: string;
  businessWebsite?: string;
  whatsappNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  townId?: string;
  countyId?: string;
  postalCode?: string;
  landmark?: string;
  latitude?: number;
  longitude?: number;
  is24Hours?: boolean;
  foundedYear?: number;
  employees?: number;
  certifications?: string[];
  paymentMethods?: string[];
  languages?: string[];
  socialMedia?: Record<string, string>;
}

export interface Service {
  id: string;
  name: string;
  slug: string;
  category: string;
  categoryId: string;
  categoryName?: string;
  description?: string;
  price: number;
  discountPrice?: number;
  basePrice?: number;
  estimatedDuration?: number;
  bookings: number;
  rating: number;
  isActive: boolean;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  imageUrl?: string;
  parentId?: string;
  displayOrder: number;
  isActive: boolean;
  children?: ServiceCategory[];
}

export interface ProviderService {
  id: string;
  provider_id: string;
  service_id: string;
  price?: number;
  discount_price?: number;
  is_available: boolean;
  service?: Service;
  provider?: ProviderProfileResponse;
  created_at: string;
  updated_at: string;
}
export interface UpdateBusinessHoursData {
  businessHours: {
    dayOfWeek: number;
    opensAt: string;
    closesAt: string;
    isClosed?: boolean;
  }[];
}

export interface UploadProviderImageData {
  imageUrl: string;
  imageType?: 'logo' | 'gallery' | 'certificate';
  isPrimary?: boolean;
  caption?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

// ============================================
// AXIOS INSTANCE
// ============================================

const API_URL: string = 'https://jibu24-server.onrender.com/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
    }
    
    return config;
  },
  (error: any) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Call refresh token endpoint
      const response = await axios.post<RefreshTokenResponse>(
  `https://jibu24-server.onrender.com/api/v1/auth/refresh-token`,
  { refreshToken }
);
        
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        // Store new tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        // Update authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        
        // Retry the original request with new token
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed – clear storage and redirect to login
        console.error('❌ Token refresh failed:', refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/auth/login')) {
          window.location.href = '/auth/login';
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // Log errors in development
    if (import.meta.env.NODE_ENV === 'development') {
      console.error(' API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    }
    
    return Promise.reject(error);
  }
);
// ============================================
// AUTH API FUNCTIONS
// ============================================

export const register = (data: RegisterData): Promise<AxiosResponse<AuthResponse>> => {
  return api.post('/auth/register', data);
};

export const login = (data: LoginCredentials): Promise<AxiosResponse<AuthResponse>> => {
  return api.post('/auth/login', data);
};

export const refreshToken = (refreshToken: string): Promise<AxiosResponse<RefreshTokenResponse>> => {
  return api.post('/auth/refresh-token', { refreshToken });
};

export const logout = (): Promise<AxiosResponse<{ message: string }>> => {
  return api.post('/auth/logout');
};

export const getMe = (): Promise<AxiosResponse<User>> => {
  return api.get('/auth/me');
};

export const forgotPassword = (email: string): Promise<AxiosResponse<{ message: string }>> => {
  return api.post('/auth/forgot-password', { email });
};

export const resetPassword = (data: { token: string; newPassword: string }): Promise<AxiosResponse<{ message: string }>> => {
  return api.post('/auth/reset-password', data);
};

export const verifyEmail = (token: string): Promise<AxiosResponse<{ message: string }>> => {
  return api.post('/auth/verify-email', { token });
};

// ============================================
// PROVIDER PROFILE API FUNCTIONS
// NOTE: paths assumed as /providers/profile/... — adjust if your router
// mounts these differently (send me the router file to confirm/fix).
// ============================================

export const getProviderProfile = (): Promise<AxiosResponse<ApiEnvelope<ProviderProfileResponse>>> => {
  return api.get('/providers/profile/me');
};

export const updateProviderProfile = (
  data: UpdateProviderProfileData
): Promise<AxiosResponse<ApiEnvelope<ProviderProfileResponse>>> => {
  return api.put('/providers/profile/me', data);
};

export const deleteProviderImage = (imageId: string): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return api.delete(`/providers/profile/images/${imageId}`);
};







export const updateBusinessHours = (
  data: UpdateBusinessHoursData
): Promise<AxiosResponse<ApiEnvelope<ProviderBusinessHour[]>>> => {
  return api.put('/providers/profile/business-hours', data);
};

export const uploadProviderImage = (
  data: { image: File; imageType?: 'logo' | 'gallery' | 'certificate'; isPrimary?: boolean; caption?: string }
): Promise<AxiosResponse<ApiEnvelope<ProviderImageItem>>> => {
  const formData = new FormData();
  formData.append('image', data.image);
  if (data.imageType) formData.append('imageType', data.imageType);
  // Convert boolean to string for FormData
  if (data.isPrimary !== undefined) {
    formData.append('isPrimary', String(data.isPrimary)); // ✅ Convert to string
  }
  if (data.caption) formData.append('caption', data.caption);
  
  return api.post('/providers/profile/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const getServiceCategories = (): Promise<AxiosResponse<ApiEnvelope<ServiceCategory[]>>> => {
  return api.get('/categories');
};
/**
 * Upload multiple images
 */
export const uploadMultipleProviderImages = (
  data: { images: File[]; imageType?: 'logo' | 'gallery' | 'certificate' }
): Promise<AxiosResponse<ApiEnvelope<ProviderImageItem[]>>> => {
  const formData = new FormData();
  data.images.forEach((image) => {
    formData.append('images', image);
  });
  if (data.imageType) formData.append('imageType', data.imageType);
  
  return api.post('/providers/profile/images/multiple', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Upload avatar
 */
export const uploadProviderAvatar = (
  data: { image: File }
): Promise<AxiosResponse<ApiEnvelope<{ avatarUrl: string; publicId: string }>>> => {
  const formData = new FormData();
  formData.append('image', data.image);
  
  return api.post('/providers/profile/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};


export const getProviderImages = (
  params?: { imageType?: string }
): Promise<AxiosResponse<ApiEnvelope<ProviderImageItem[]>>> => {
  return api.get('/providers/profile/images', { params });
};

/**
 * Set image as primary
 */
export const setPrimaryImage = (
  imageId: string
): Promise<AxiosResponse<ApiEnvelope<ProviderImageItem>>> => {
  return api.patch(`/providers/profile/images/${imageId}/primary`);
};
// src/services/api.ts - Add these functions

// ============================================
// PROVIDER DASHBOARD API FUNCTIONS
// ============================================

/**
 * Get provider dashboard stats
 */
export const getProviderStats = (): Promise<AxiosResponse<ApiEnvelope<{
  totalBookings: number;
  completedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  bookingGrowth: number;
  revenueGrowth: number;
  views: number;
  conversionRate: number;
}>>> => {
  return api.get('/providers/stats');
};

/**
 * Get provider bookings with filters
 */
export const getProviderBookings = (params?: {
  status?: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'all';
  search?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}): Promise<AxiosResponse<ApiEnvelope<Booking[]>>> => {
  return api.get('/providers/bookings', { params });
};

/**
 * Get booking details by ID
 */
export const getBookingDetails = (bookingId: string): Promise<AxiosResponse<ApiEnvelope<Booking>>> => {
  return api.get(`/providers/bookings/${bookingId}`);
};

/**
 * Update booking status
 */
export const updateBookingStatus = (
  bookingId: string,
  status:'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'
): Promise<AxiosResponse<ApiEnvelope<Booking>>> => {
  return api.patch(`/providers/bookings/${bookingId}/status`, { status });
};
export const addService = (data: AddServiceData): Promise<AxiosResponse<ApiEnvelope<{ 
  service: Service; 
  providerService: ProviderService; 
  payment: any 
}>>> => {
  return api.post('/services', data);
};

/**
 * Update a service (provider)
 */
export const updateService = (
  serviceId: string,
  data: UpdateServiceData
): Promise<AxiosResponse<ApiEnvelope<ProviderService>>> => {
  return api.put(`/services/${serviceId}`, data);
};

/**
 * Delete a service (provider)
 */
export const deleteService = (serviceId: string): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return api.delete(`/services/${serviceId}`);
};

/**
 * Get provider's own services
 */

/**
 * Get service details by ID
 */
export const getServiceDetails = (serviceId: string): Promise<AxiosResponse<ApiEnvelope<ProviderService>>> => {
  return api.get(`/services/${serviceId}`);
};

/**
 * Get all services (admin only)
 */
export const getAllServices = (params?: {
  status?: 'pending' | 'active' | 'all';
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<AxiosResponse<ApiEnvelope<ProviderService[]>>> => {
  return api.get('/services/admin/all', { params });
};

/**
 * Approve a service (admin)
 */
export const approveService = (serviceId: string): Promise<AxiosResponse<ApiEnvelope<ProviderService>>> => {
  return api.post(`/services/admin/${serviceId}/approve`);
};

/**
 * Reject a service (admin)
 */
export const rejectService = (
  serviceId: string,
  data?: ServiceApprovalData
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return api.post(`/services/admin/${serviceId}/reject`, data || {});
};

/**
 * Admin delete a service (admin)
 */
export const adminDeleteService = (serviceId: string): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return api.delete(`/services/admin/${serviceId}`);
};
/**
 * Get provider services
 */
export const getProviderServicesList = (): Promise<AxiosResponse<ApiEnvelope<Service[]>>> => {
  return api.get('/providers/services');
};

/**
 * Get provider reviews
 */
export const getProviderReviews = (params?: {
  page?: number;
  limit?: number;
  rating?: number;
}): Promise<AxiosResponse<ApiEnvelope<Review[]>>> => {
  return api.get('/providers/reviews', { params });
};

/**
 * Respond to a review
 */
export const respondToReview = (
  reviewId: string,
  response: string
): Promise<AxiosResponse<ApiEnvelope<Review>>> => {
  return api.post(`/providers/reviews/${reviewId}/respond`, { response });
};

/**
 * Get provider analytics
 */
export const getProviderAnalytics = (params?: {
  period?: 'week' | 'month' | 'year';
}): Promise<AxiosResponse<ApiEnvelope<{
  overview: Analytics;
  bookingTrend: Array<{ date: string; bookings: number; revenue: number }>;
  categoryDistribution: Array<{ name: string; value: number }>;
  revenueBreakdown: Array<{ name: string; value: number }>;
}>>> => {
  return api.get('/providers/analytics', { params });
};
// ============================================
// HELPER FUNCTIONS
// ============================================

interface AuthData {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export const setAuthData = (data: AuthData): void => {
  if (data.accessToken) localStorage.setItem('accessToken', data.accessToken);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
};

export const clearAuthData = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

export const getStoredUser = (): User | null => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default api;